'use client'

import { useState } from 'react'
import { ArrowRightIcon } from '@heroicons/react/24/outline'
import { Modal } from '@/Modal'
import { Button } from '@/General/Button'
import { useStore } from '@/store'
import { useAddRestockAlertMutation } from '../../graphql/generated/graphql'

export const EmailNotificationModal = ({
    isOpen,
    onClose,
    productId,
    productName,
    selectedOptionId,
}: EmailNotificationModalProps) => {
    const [isSubmitted, setIsSubmitted] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const user = useStore(state => state.user)
    const ecommBrandId = useStore(state => state.ecommBrandId)
    
    const { mutate: addRestockAlert, isPending, error: mutationError } = useAddRestockAlertMutation({
        onSuccess: () => setIsSubmitted(true)
    })

    const handleConfirm = () => {
        if (!user?.id) {
            setError('Please log in to set up restock alerts')
            return
        }

        setError(null)

        addRestockAlert({
            productId,
            optionId: selectedOptionId ?? undefined,
            brandId: ecommBrandId ?? undefined,
        })
    }

    const handleClose = () => {
        setIsSubmitted(false)
        setError(null)
        onClose()
    }

    if (isSubmitted) {
        return (
            <Modal isOpen={isOpen} onClose={handleClose}>
                <div className="relative">
                    <div className="relative bg-gradient-to-br from-green-50 to-emerald-50 px-6 pt-8 pb-6 overflow-hidden">
                        <div className="flex flex-col items-center">
                            <div className="mt-4 text-center transition-all duration-200 opacity-100">
                                <h3 className="text-2xl font-bold text-gray-900 mb-1">
                                    Email Notification Set!
                                </h3>
                                <p className="text-green-700 font-medium">We'll notify you when it's back in stock</p>
                            </div>
                        </div>
                    </div>

                    <div className="px-6 py-6">
                        <div className="text-center">
                            <p className="text-gray-500 mb-6">
                                We'll send you an email at <strong>{user?.email}</strong> when {productName} is back in stock.
                            </p>
                            <Button onClick={handleClose} color="green" className="w-full">
                                Close
                            </Button>
                        </div>
                    </div>
                </div>
            </Modal>
        )
    }

    return (
        <Modal isOpen={isOpen} onClose={handleClose}>
            <div className="relative">
                <div className="relative bg-gradient-to-br from-blue-50 to-indigo-50 px-6 pt-8 pb-6 overflow-hidden">
                    <div className="flex flex-col items-center">
                        <div className="mt-4 text-center transition-all duration-200 opacity-100">
                            <h3 className="text-2xl font-bold text-gray-900 mb-1">
                                {productName}
                            </h3>
                            <p className="text-blue-700 font-medium">Get notified when back in stock</p>
                        </div>
                    </div>
                </div>

                <div className="px-6 py-6">
                    <div className="text-center mb-6">
                        <p className="text-gray-500">
                            We'll send you an email when this product is back in stock.
                        </p>
                    </div>

                    <div className="space-y-4">
                        {(error || !!mutationError) && (
                            <div className="bg-red-50 border border-red-200 rounded-md p-3">
                                <p className="text-sm text-red-600">{error || 'Failed to set up restock alert. Please try again.'}</p>
                            </div>
                        )}
                        
                        <Button
                            onClick={handleConfirm}
                            color="green"
                            className="w-full"
                            disabled={!user?.id || isPending}
                        >
                            {isPending ? 'Setting up...' : (!user?.id ? 'Please log in' : 'Yes, notify me')}
                            {!isPending && <ArrowRightIcon className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform duration-200" />}
                        </Button>
                    </div>
                </div>
            </div>
        </Modal>
    )
}

interface EmailNotificationModalProps {
    isOpen: boolean
    onClose: () => void
    productId: number
    productName: string
    selectedOptionId?: number
}
